import { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../../reports/edit/editApi';
import { previewRecalc } from './revenueApi';
import type { RecalcSummary } from './revenueApi';

// Дата вводится по цифрам: запрос уходит, когда пользователь перестал менять ее
const DEBOUNCE_MS = 350;

interface PreviewResult {
  from: string;
  attempt: number;
  summary: RecalcSummary | null;
  error: string;
}

/**
 * Предпросмотр пересчета истории с даты from. Пока from пуст или enabled = false, запрос не уходит.
 * Ответ на устаревшую дату отбрасывается, а показывается только результат для текущей.
 */
export function useRecalcPreview(from: string, enabled: boolean) {
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !from) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const summary = await previewRecalc(from);
        if (!cancelled) setResult({ from, attempt, summary, error: '' });
      } catch (err) {
        if (!cancelled) {
          setResult({ from, attempt, summary: null, error: apiErrorMessage(err, 'Не удалось построить предпросмотр. Повторите попытку') });
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [from, enabled, attempt]);

  const retry = useCallback(() => setAttempt((prev) => prev + 1), []);

  const ready = !!result && result.from === from && result.attempt === attempt;
  return {
    summary: ready && result ? result.summary : null,
    error: ready && result ? result.error : '',
    loading: enabled && from !== '' && !ready,
    retry
  };
}
